import React, { useState, useEffect } from 'react';
import { Radio, DatePicker } from 'antd';
import type { RadioChangeEvent } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ScaleQueryType } from '../../graphql/types/dashboard/ScaleDashboardTypes';
import { DASHBOARD_PACIFIC_TZ } from '../../utils/dashboardPacificTime';
import { isDashboardDateDisabled } from './dashboardDateRule';

dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;

export interface TimeRange {
    queryType: ScaleQueryType;
    startDate: string; // YYYY-MM-DD
    endDate: string; // YYYY-MM-DD
    startHour?: number; // 0-23, 仅在日维度有效
    endHour?: number; // 0-23, 仅在日维度有效
    todayRealtime?: boolean; // 今日实时标志
}

interface TimeRangeFilterProps {
    onChange: (timeRange: TimeRange) => void;
    /** 自定义模式下，最早可选日期 = 今天 - maxDaysBack。不传则不限制。 */
    maxDaysBack?: number;
    /**
     * 预设选项配置（可选）。
     * 典型用法：某些看板的「日」维度只想保留「近15日/自定义」。
     */
    presetKeysByQueryType?: Partial<Record<ScaleQueryType, PresetKey[]>>;
    /**
     * 默认选中的预设（可选）。
     */
    defaultPresetByQueryType?: Partial<Record<ScaleQueryType, PresetKey>>;
    /**
     * 是否显示小时范围选择器（仅分拣看板传 true）。
     * - false（默认）：原有行为，「今日实时」作为日维度的预设选项，不显示小时选择
     * - true：「今日实时」提升到与日/周/月并列；日维度使用日期+小时合并 RangePicker
     */
    showHourRange?: boolean;
    /**
     * 看板统计时区（如 America/Los_Angeles）。传入后「今日/昨日/上周」等预设均按该时区计算，与浏览器时区无关。
     */
    dashboardTimezone?: string;
}

type PresetKey = 'yesterday' | 'last3days' | 'last7days' | 'last15days' | 'todayRealtime' | 'custom' |
    'lastweek' | 'last4weeks' | 'last8weeks' |
    'lastmonth' | 'last3months' | 'last6months';

// showHourRange=true 时的顶层模式
type TopMode = 'todayRealtime' | ScaleQueryType;

const radioGroupStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
};

const TimeRangeFilter: React.FC<TimeRangeFilterProps> = ({
                                                             onChange,
                                                             maxDaysBack,
                                                             presetKeysByQueryType,
                                                             defaultPresetByQueryType,
                                                             showHourRange = false,
                                                             dashboardTimezone,
                                                         }) => {
    const tz = dashboardTimezone ?? DASHBOARD_PACIFIC_TZ;
    const nowInDashboardTz = () => (dashboardTimezone ? dayjs().tz(tz) : dayjs());

    const [queryType, setQueryType] = useState<ScaleQueryType>(ScaleQueryType.DAILY);
    const [hoverDate, setHoverDate] = useState<Dayjs | null>(null);
    const isUpdatingCustomRangeRef = React.useRef(false);

    // showHourRange=true 时，topMode 可以是 'todayRealtime'
    const [topMode, setTopMode] = useState<TopMode>(ScaleQueryType.DAILY);
    const isTodayRealtime = showHourRange && topMode === 'todayRealtime';

    // ----------------------------- 预设选项 -----------------------------

    const getAllPresetOptionsByQueryType = (type: ScaleQueryType): { label: string; value: PresetKey }[] => {
        if (type === ScaleQueryType.DAILY) {
            const base: { label: string; value: PresetKey }[] = [
                { label: '昨日', value: 'yesterday' },
                { label: '近3日', value: 'last3days' },
                { label: '近7日', value: 'last7days' },
                { label: '近15日', value: 'last15days' },
                { label: '自定义', value: 'custom' },
            ];
            // showHourRange=false 时，今日实时作为预设选项展示在此处
            if (!showHourRange) {
                return [{ label: '今日实时', value: 'todayRealtime' }, ...base];
            }
            return base;
        }
        if (type === ScaleQueryType.WEEKLY) {
            return [
                { label: '上周', value: 'lastweek' },
                { label: '近4周', value: 'last4weeks' },
                { label: '近8周', value: 'last8weeks' },
                { label: '自定义', value: 'custom' },
            ];
        }
        return [
            { label: '上月', value: 'lastmonth' },
            { label: '近3月', value: 'last3months' },
            { label: '近6月', value: 'last6months' },
            { label: '自定义', value: 'custom' },
        ];
    };

    const getPresetOptions = (type: ScaleQueryType) => {
        const all = getAllPresetOptionsByQueryType(type);
        const allowedKeys = presetKeysByQueryType?.[type];
        if (!allowedKeys || allowedKeys.length === 0) return all;
        const allowedSet = new Set<PresetKey>(allowedKeys);
        return all.filter(o => allowedSet.has(o.value));
    };

    const getDefaultPreset = (type: ScaleQueryType): PresetKey => {
        const candidate = defaultPresetByQueryType?.[type];
        const options = getPresetOptions(type);
        if (candidate && options.some(o => o.value === candidate)) return candidate;
        // 日维度默认昨日，避免「今日实时」排第一时变成默认
        if (type === ScaleQueryType.DAILY) {
            const yd = options.find(o => o.value === 'yesterday');
            if (yd) return 'yesterday';
        }
        return options[0]?.value ?? 'custom';
    };

    const [preset, setPreset] = useState<PresetKey>(() => getDefaultPreset(ScaleQueryType.DAILY));

    // ----------------------------- 默认自定义范围 -----------------------------

    const getDefaultCustomRange = (type: ScaleQueryType): [Dayjs, Dayjs] => {
        const today = nowInDashboardTz();
        const yesterday = today.subtract(1, 'day');
        if (type === ScaleQueryType.DAILY) return [yesterday.subtract(2, 'day'), yesterday];
        if (type === ScaleQueryType.WEEKLY) {
            const week4End = today.subtract(1, 'week').endOf('isoWeek').startOf('day');
            return [week4End.subtract(3, 'week').startOf('isoWeek'), week4End];
        }
        const month3End = today.subtract(1, 'month').endOf('month').startOf('day');
        return [month3End.subtract(2, 'month').startOf('month'), month3End];
    };

    const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(getDefaultCustomRange(ScaleQueryType.DAILY));
    const [startHour, setStartHour] = useState<number>(0);
    const [endHour, setEndHour] = useState<number>(23);

    // ----------------------------- 计算时间范围 -----------------------------

    const calculateTimeRange = (type: ScaleQueryType, presetKey: PresetKey, customDates?: [Dayjs, Dayjs] | null): TimeRange => {
        let startDate: Dayjs;
        let endDate: Dayjs;
        let todayRealtime = false;

        if (presetKey === 'todayRealtime' && type === ScaleQueryType.DAILY) {
            const today = nowInDashboardTz();
            startDate = today;
            endDate = today;
            todayRealtime = true;
        } else if (presetKey === 'custom' && customDates) {
            startDate = customDates[0];
            endDate = customDates[1];
            if (type === ScaleQueryType.WEEKLY) {
                startDate = startDate.startOf('isoWeek');
                endDate = endDate.endOf('isoWeek').startOf('day');
            } else if (type === ScaleQueryType.MONTHLY) {
                startDate = startDate.startOf('month');
                endDate = endDate.endOf('month').startOf('day');
            }
        } else {
            const today = nowInDashboardTz();
            const yesterday = today.subtract(1, 'day');
            switch (presetKey) {
                case 'yesterday':
                    startDate = yesterday; endDate = yesterday; break;
                case 'last3days':
                    startDate = yesterday.subtract(2, 'day'); endDate = yesterday; break;
                case 'last7days':
                    startDate = yesterday.subtract(6, 'day'); endDate = yesterday; break;
                case 'last15days':
                    startDate = yesterday.subtract(14, 'day'); endDate = yesterday; break;
                case 'lastweek': {
                    startDate = today.subtract(1, 'week').startOf('isoWeek');
                    endDate = today.subtract(1, 'week').endOf('isoWeek').startOf('day');
                    break;
                }
                case 'last4weeks': {
                    const w4End = today.subtract(1, 'week').endOf('isoWeek').startOf('day');
                    startDate = w4End.subtract(3, 'week').startOf('isoWeek');
                    endDate = w4End;
                    break;
                }
                case 'last8weeks': {
                    const w8End = today.subtract(1, 'week').endOf('isoWeek').startOf('day');
                    startDate = w8End.subtract(7, 'week').startOf('isoWeek');
                    endDate = w8End;
                    break;
                }
                case 'lastmonth':
                    startDate = today.subtract(1, 'month').startOf('month');
                    endDate = today.subtract(1, 'month').endOf('month').startOf('day');
                    break;
                case 'last3months':
                    startDate = today.subtract(3, 'month').startOf('month');
                    endDate = today.subtract(1, 'month').endOf('month').startOf('day');
                    break;
                case 'last6months':
                    startDate = today.subtract(6, 'month').startOf('month');
                    endDate = today.subtract(1, 'month').endOf('month').startOf('day');
                    break;
                default:
                    startDate = yesterday.subtract(2, 'day'); endDate = yesterday;
            }
        }

        const result: TimeRange = {
            queryType: type,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
        };
        if (type === ScaleQueryType.DAILY) {
            result.startHour = startHour;
            result.endHour = endHour;
            result.todayRealtime = todayRealtime;
        }
        return result;
    };

    // ----------------------------- Effects -----------------------------

    useEffect(() => {
        // showHourRange=true 且选了今日实时
        if (isTodayRealtime) {
            const today = nowInDashboardTz();
            onChange({
                queryType: ScaleQueryType.DAILY,
                startDate: today.format('YYYY-MM-DD'),
                endDate: today.format('YYYY-MM-DD'),
                startHour: 0,
                endHour: today.hour(),
                todayRealtime: true,
            });
            return;
        }
        const timeRange = calculateTimeRange(queryType, preset, customRange);
        onChange(timeRange);
        if (preset !== 'custom') {
            isUpdatingCustomRangeRef.current = true;
            setCustomRange([dayjs(timeRange.startDate), dayjs(timeRange.endDate)]);
            setTimeout(() => { isUpdatingCustomRangeRef.current = false; }, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryType, preset, startHour, endHour, isTodayRealtime]);

    useEffect(() => {
        if (isUpdatingCustomRangeRef.current) return;
        if (preset === 'custom' && customRange && !isTodayRealtime) {
            onChange(calculateTimeRange(queryType, preset, customRange));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customRange]);

    // ----------------------------- 事件处理 -----------------------------

    const handleModeChange = (value: TopMode) => {
        setTopMode(value);
        if (value === 'todayRealtime') return;
        const newType = value as ScaleQueryType;
        if (newType !== queryType) {
            setQueryType(newType);
            const defaultPreset = getDefaultPreset(newType);
            setPreset(defaultPreset);
            setCustomRange(getDefaultCustomRange(newType));
            // 切换 queryType 时若默认预设非自定义，重置小时窗为全天 0~23
            if (defaultPreset !== 'custom') {
                setStartHour(0);
                setEndHour(23);
            }
        }
    };

    const handleQueryTypeChange = (e: RadioChangeEvent) => {
        if (showHourRange) {
            handleModeChange(e.target.value as TopMode);
        } else {
            const newType = e.target.value as ScaleQueryType;
            setQueryType(newType);
            if (preset === 'custom' && customRange) {
                let s = customRange[0];
                let en = customRange[1];
                if (newType === ScaleQueryType.WEEKLY) {
                    s = s.startOf('isoWeek');
                    en = en.endOf('isoWeek').startOf('day');
                } else if (newType === ScaleQueryType.MONTHLY) {
                    s = s.startOf('month');
                    en = en.endOf('month').startOf('day');
                }
                setCustomRange([s, en]);
            }
            setPreset(getDefaultPreset(newType));
        }
    };

    const handlePresetChange = (value: PresetKey) => {
        setPreset(value);
        // 切到非自定义预设（昨日/近3日/近7日/近15日）时重置小时窗为全天 0~23，
        // 避免沿用上次自定义时段（如 9~12）导致预设语义被污染
        if (value !== 'custom') {
            setStartHour(0);
            setEndHour(23);
        }
    };

    const handleCustomRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        if (dates && dates[0] && dates[1]) {
            let s = dates[0];
            let en = dates[1];
            if (queryType === ScaleQueryType.WEEKLY) {
                s = s.startOf('isoWeek');
                en = en.endOf('isoWeek').startOf('day');
            } else if (queryType === ScaleQueryType.MONTHLY) {
                s = s.startOf('month');
                en = en.endOf('month').startOf('day');
            }
            setCustomRange([s, en]);
            if (preset !== 'custom') setPreset('custom');
        }
    };

    // 合并日期+小时 RangePicker 的变更处理（showHourRange=true 时的日维度）
    const handleCombinedRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        if (dates && dates[0] && dates[1]) {
            const [start, end] = dates;
            const newStartDate = start.startOf('day');
            const newEndDate = end.startOf('day');
            const newStartHour = start.hour();
            const newEndHour = end.hour();
            // 先用 ref 屏蔽 customRange effect，避免重复触发
            isUpdatingCustomRangeRef.current = true;
            setCustomRange([newStartDate, newEndDate]);
            setStartHour(newStartHour);
            setEndHour(newEndHour);
            if (preset !== 'custom') setPreset('custom');
            setTimeout(() => { isUpdatingCustomRangeRef.current = false; }, 0);
            // 直接触发查询，避免依赖 effect 链路（preset 已是 custom 且 hours 未变时 effect 不会重新执行）
            onChange({
                queryType,
                startDate: newStartDate.format('YYYY-MM-DD'),
                endDate: newEndDate.format('YYYY-MM-DD'),
                startHour: newStartHour,
                endHour: newEndHour,
                todayRealtime: false,
            });
        }
    };

    // ----------------------------- 禁用日期 -----------------------------

    // 合并选择器：允许今日，禁止明日及以后（按日期标签比较，tz 对象加减天数跨夏令时的时刻偏差不影响）
    const disabledDateForCombined = (current: Dayjs) =>
        isDashboardDateDisabled(current, nowInDashboardTz(), { latestIsToday: true, maxDaysBack });

    // 普通选择器：昨日为最新可选日
    const disabledDate = (current: Dayjs) =>
        isDashboardDateDisabled(current, nowInDashboardTz(), { latestIsToday: false, maxDaysBack });

    // ----------------------------- 周高亮渲染 -----------------------------

    const dateRender = (current: Dayjs) => {
        if (queryType === ScaleQueryType.WEEKLY) {
            let shouldHighlight = false;
            if (customRange && customRange[0]) {
                const [start, end] = customRange;
                if (start && end) {
                    shouldHighlight =
                        (current.isAfter(start.startOf('isoWeek'), 'day') || current.isSame(start.startOf('isoWeek'), 'day')) &&
                        (current.isBefore(end.endOf('isoWeek').startOf('day'), 'day') || current.isSame(end.endOf('isoWeek').startOf('day'), 'day'));
                } else if (start) {
                    shouldHighlight =
                        (current.isAfter(start.startOf('isoWeek'), 'day') || current.isSame(start.startOf('isoWeek'), 'day')) &&
                        (current.isBefore(start.endOf('isoWeek').startOf('day'), 'day') || current.isSame(start.endOf('isoWeek').startOf('day'), 'day'));
                }
            }
            if (!shouldHighlight && hoverDate) {
                shouldHighlight =
                    (current.isAfter(hoverDate.startOf('isoWeek'), 'day') || current.isSame(hoverDate.startOf('isoWeek'), 'day')) &&
                    (current.isBefore(hoverDate.endOf('isoWeek').startOf('day'), 'day') || current.isSame(hoverDate.endOf('isoWeek').startOf('day'), 'day'));
            }
            if (shouldHighlight) {
                return (
                    <div
                        className="ant-picker-cell-inner"
                        style={{ backgroundColor: '#e6d9f5', borderRadius: '2px' }}
                        onMouseEnter={() => setHoverDate(current)}
                        onMouseLeave={() => setHoverDate(null)}
                    >
                        {current.date()}
                    </div>
                );
            }
        }
        return (
            <div
                className="ant-picker-cell-inner"
                onMouseEnter={() => queryType === ScaleQueryType.WEEKLY && setHoverDate(current)}
                onMouseLeave={() => queryType === ScaleQueryType.WEEKLY && setHoverDate(null)}
            >
                {current.date()}
            </div>
        );
    };

    useEffect(() => {
        if (isTodayRealtime) return;
        const options = getPresetOptions(queryType);
        if (options.length === 0) return;
        if (!options.some(o => o.value === preset)) {
            setPreset(getDefaultPreset(queryType));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryType, presetKeysByQueryType]);

    const getPickerType = () => (queryType === ScaleQueryType.MONTHLY ? 'month' : 'date');

    // showHourRange=true 且在日维度时使用合并选择器
    const isDailyWithHour = !isTodayRealtime && queryType === ScaleQueryType.DAILY && showHourRange;

    // 合并选择器的值
    const combinedPickerValue: [Dayjs, Dayjs] | null = customRange
        ? [customRange[0].hour(startHour).minute(0).second(0), customRange[1].hour(endHour).minute(0).second(0)]
        : null;

    // ----------------------------- 渲染 -----------------------------

    // showHourRange=true 时与 showHourRange=false 一致采用 flex 自然宽度，避免大屏下按钮被等分拉宽；
    // 今日实时拆为独立按钮组放在左侧，日/周/月单独一组
    if (showHourRange) {
        return (
            <div className="flex items-center gap-4 flex-wrap font-semibold text-gray-800">
                {/* 今日实时（独立按钮组） */}
                <Radio.Group
                    value={topMode === 'todayRealtime' ? 'todayRealtime' : undefined}
                    onChange={(e) => handleModeChange(e.target.value as TopMode)}
                    style={radioGroupStyle}
                >
                    <Radio.Button value="todayRealtime">今日实时</Radio.Button>
                </Radio.Group>

                {/* 日 / 周 / 月 */}
                <Radio.Group
                    value={topMode === 'todayRealtime' ? undefined : queryType}
                    onChange={(e) => handleModeChange(e.target.value as TopMode)}
                    style={radioGroupStyle}
                >
                    <Radio.Button value={ScaleQueryType.DAILY}>日</Radio.Button>
                    <Radio.Button value={ScaleQueryType.WEEKLY}>周</Radio.Button>
                    <Radio.Button value={ScaleQueryType.MONTHLY}>月</Radio.Button>
                </Radio.Group>

                {/* 快捷时间预设（今日实时时隐藏） */}
                {!isTodayRealtime && (
                    <Radio.Group
                        value={preset}
                        onChange={(e) => handlePresetChange(e.target.value)}
                        style={radioGroupStyle}
                    >
                        {getPresetOptions(queryType).map(option => (
                            <Radio.Button key={option.value} value={option.value}>
                                {option.label}
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                )}

                {/* 日期（+小时）选择器（今日实时时隐藏） */}
                {!isTodayRealtime && (
                    isDailyWithHour ? (
                        <RangePicker
                            value={combinedPickerValue}
                            onChange={handleCombinedRangeChange as any}
                            disabledDate={disabledDateForCombined}
                            format="YYYY/MM/DD HH"
                            showTime={{
                                format: 'HH',
                                defaultValue: [dayjs('00', 'HH'), dayjs('23', 'HH')],
                            }}
                            disabled={preset !== 'custom'}
                        />
                    ) : (
                        <RangePicker
                            value={customRange}
                            onChange={handleCustomRangeChange as any}
                            disabledDate={disabledDate}
                            dateRender={queryType === ScaleQueryType.WEEKLY ? dateRender : undefined}
                            picker={getPickerType() as any}
                            format={queryType === ScaleQueryType.MONTHLY ? 'YYYY-MM' : 'YYYY-MM-DD'}
                            disabled={preset !== 'custom'}
                            inputReadOnly={preset !== 'custom'}
                        />
                    )
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 font-semibold text-gray-800">
            <div className="flex items-center gap-4 flex-nowrap">
                <Radio.Group
                    value={queryType}
                    onChange={handleQueryTypeChange}
                    style={radioGroupStyle}
                >
                    <Radio.Button value={ScaleQueryType.DAILY}>日</Radio.Button>
                    <Radio.Button value={ScaleQueryType.WEEKLY}>周</Radio.Button>
                    <Radio.Button value={ScaleQueryType.MONTHLY}>月</Radio.Button>
                </Radio.Group>

                <Radio.Group
                    value={preset}
                    onChange={(e) => handlePresetChange(e.target.value)}
                    style={radioGroupStyle}
                >
                    {getPresetOptions(queryType).map(option => (
                        <Radio.Button key={option.value} value={option.value}>
                            {option.label}
                        </Radio.Button>
                    ))}
                </Radio.Group>

                <div className="flex items-center gap-2 flex-nowrap">
                    <RangePicker
                        value={customRange}
                        onChange={handleCustomRangeChange as any}
                        disabledDate={disabledDate}
                        dateRender={queryType === ScaleQueryType.WEEKLY ? dateRender : undefined}
                        picker={getPickerType() as any}
                        format={queryType === ScaleQueryType.MONTHLY ? 'YYYY-MM' : 'YYYY-MM-DD'}
                        disabled={preset !== 'custom'}
                        inputReadOnly={preset !== 'custom'}
                        style={{ minWidth: '280px' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default TimeRangeFilter;
